/**
 * Lazy gate in front of the 14-language dictionary.
 *
 * Why: dictionary.js is 86 KB of source and landed as roughly the tail 45 KB
 * of a 373 KB main.js — over 10% of the parse-blocking main bundle — because
 * T.js and domTranslator.js are used site-wide and imported it statically.
 * The site always opens in English (see detectInitialLanguage in
 * I18nContext.js) and English IS the source text, so nearly every visitor
 * downloaded and parsed those 45 KB to translate nothing.
 *
 * Contract: translateText / translateWithEmojiPrefix keep the exact
 * signatures dictionary.js exported and return their input unchanged while
 * no dictionary is loaded — the same thing the old code did for lang === 'en'.
 * Callers that need a real translation must await loadDictionary() first;
 * I18nContext does that on every switch to a non-English language.
 */

/** Loaded dictionary module, or null while we're still on English. */
let dict = null;
/** In-flight import, so N callers during one switch share one chunk fetch. */
let pending = null;

/**
 * Pull the dictionary chunk in. Resolves to the module (or null if the
 * fetch failed). Safe to call repeatedly — idempotent after the first hit.
 */
export function loadDictionary() {
  if (dict) return Promise.resolve(dict);
  if (!pending) {
    pending = import(/* webpackChunkName: "i18n-dictionary" */ './dictionary')
      .then((mod) => {
        dict = mod;
        return dict;
      })
      .catch((err) => {
        // Chunk fetch failed (offline, or a stale deploy whose hashed chunk
        // is gone). Clear `pending` so a later switch retries, and let the
        // page stay in English rather than throwing — domTranslator's
        // AI-translation fallback still covers the text.
        pending = null;
        console.warn('[i18n] dictionary chunk failed to load', err);
        return null;
      });
  }
  return pending;
}

export function translateText(englishText, lang) {
  if (!dict || lang === 'en') return englishText;
  return dict.translateText(englishText, lang);
}

export function translateWithEmojiPrefix(text, lang) {
  if (!dict || lang === 'en') return text;
  return dict.translateWithEmojiPrefix(text, lang);
}
